import { useQueryClient } from '@tanstack/react-query';
import {
  Coins,
  Download,
  KeyRound,
  MoreHorizontal,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Users,
} from 'lucide-react';
import { useState } from 'react';

import { useAmbientesControllerListar } from '@/api/generated/admin-ambientes/admin-ambientes';
import {
  getUsuariosControllerListarQueryKey,
  useUsuariosControllerDefinirAtivo,
  useUsuariosControllerListar,
} from '@/api/generated/admin-usuarios/admin-usuarios';
import type { UsuarioResponse } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { CargoDoUsuarioDialogo } from '@/features/admin/usuarios/components/CargoDoUsuarioDialogo';
import { CreditosDoUsuarioDialogo } from '@/features/admin/usuarios/components/CreditosDoUsuarioDialogo';
import { ExportarUsuariosDialogo } from '@/features/admin/usuarios/components/ExportarUsuariosDialogo';
import { UsuarioFormDialogo } from '@/features/admin/usuarios/components/UsuarioFormDialogo';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Input } from '@/shared/ui/input';
import { Paginacao } from '@/shared/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { toast } from '@/shared/ui/sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

const TODOS = 'todos';

/**
 * Usuários (F6-1).
 *
 * ⚠️ Não existe excluir: "excluir" é desativar. O perfil não tem exclusão
 * lógica, e apagar de verdade deixaria a linha de autenticação órfã — o usuário
 * sumiria da lista e continuaria conseguindo logar.
 */
export function UsuariosPage() {
  const { pode } = usePermissoes();
  const { me } = useAuth();
  const queryClient = useQueryClient();

  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [ambienteId, setAmbienteId] = useState<string>(TODOS);
  const [situacao, setSituacao] = useState<string>(TODOS);
  const [exportarAberto, setExportarAberto] = useState(false);
  const [formAberto, setFormAberto] = useState(false);
  const [emEdicao, setEmEdicao] = useState<UsuarioResponse | undefined>();
  const [cargoDe, setCargoDe] = useState<UsuarioResponse | null>(null);
  const [creditosDe, setCreditosDe] = useState<UsuarioResponse | null>(null);
  const [aDesativar, setADesativar] = useState<UsuarioResponse | null>(null);

  const { data, isPending, isError, refetch } = useUsuariosControllerListar({
    pagina,
    porPagina: 20,
    ordenarPor: 'nomeCompleto',
    ordem: 'asc',
    ...(busca.trim() ? { busca: busca.trim() } : {}),
    ...(ambienteId !== TODOS ? { tenantId: ambienteId } : {}),
    ...(situacao !== TODOS ? { ativo: situacao as 'true' | 'false' } : {}),
  });

  // O filtro por ambiente é um parâmetro explícito da tela — nunca o ambiente
  // da sessão do admin, que é só o endereço pessoal dele no sistema.
  const { data: ambientes } = useAmbientesControllerListar(
    { pagina: 1, porPagina: 100, ordenarPor: 'nome', ordem: 'asc' },
    { query: { enabled: pode('admin_tenants.visualizar') } },
  );

  const definirAtivo = useUsuariosControllerDefinirAtivo();

  const podeCriar = pode('admin_usuarios.criar');
  const podeEditar = pode('admin_usuarios.editar');
  // Chave própria (`admin_creditos.*`), separada de `admin_usuarios.editar`:
  // dar discussão de graça é decisão comercial, não edição de cadastro.
  const podeVerCreditos = pode('admin_creditos.visualizar');

  function aoFiltrar(aplicar: () => void) {
    aplicar();
    setPagina(1);
  }

  async function alternarAtivo(usuario: UsuarioResponse, ativo: boolean) {
    try {
      await definirAtivo.mutateAsync({ id: usuario.id, data: { ativo } });
      await queryClient.invalidateQueries({ queryKey: getUsuariosControllerListarQueryKey() });
      toast.success(
        ativo ? `${usuario.nomeCompleto} reativado.` : `${usuario.nomeCompleto} desativado.`,
      );
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível alterar o usuário.');
    } finally {
      setADesativar(null);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <Input
            value={busca}
            onChange={(e) => aoFiltrar(() => setBusca(e.target.value))}
            placeholder="Buscar por nome, e-mail ou CRMV..."
            className="sm:max-w-xs"
          />

          {ambientes && (
            <Select value={ambienteId} onValueChange={(v) => aoFiltrar(() => setAmbienteId(v))}>
              <SelectTrigger className="sm:w-52" aria-label="Filtrar por ambiente">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TODOS}>Todos os ambientes</SelectItem>
                {ambientes.itens.map((ambiente) => (
                  <SelectItem key={ambiente.id} value={ambiente.id}>
                    {ambiente.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Select value={situacao} onValueChange={(v) => aoFiltrar(() => setSituacao(v))}>
            <SelectTrigger className="sm:w-40" aria-label="Filtrar por status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TODOS}>Todos os status</SelectItem>
              <SelectItem value="true">Ativos</SelectItem>
              <SelectItem value="false">Desativados</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="gap-2" onClick={() => setExportarAberto(true)}>
            <Download className="w-4 h-4" />
            Exportar CSV
          </Button>

          {podeCriar && (
            <Button
              onClick={() => {
                setEmEdicao(undefined);
                setFormAberto(true);
              }}
            >
              <Plus className="w-4 h-4" />
              Novo usuário
            </Button>
          )}
        </div>
      </div>

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-brand/5 to-transparent border-b border-brand/10">
          <h3 className="text-sm font-semibold text-brand uppercase tracking-wide">Usuários</h3>
        </div>

        {isPending ? (
          <div className="p-6 space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 rounded-md" />
            ))}
          </div>
        ) : isError || !data ? (
          <EstadoErro aoTentarDeNovo={() => void refetch()} />
        ) : data.itens.length === 0 ? (
          <EstadoVazio
            icone={Users}
            titulo="Nenhum usuário encontrado"
            descricao={busca ? 'Tente outro termo de busca.' : 'Crie o primeiro usuário.'}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Usuário</TableHead>
                    <TableHead>Ambiente</TableHead>
                    <TableHead>Cargo</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-20">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.itens.map((usuario) => (
                    <TableRow key={usuario.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-foreground">
                            {usuario.nomeCompleto}
                          </span>
                          {usuario.papel === 'SUPER_ADMIN' && (
                            <Badge variant="outline" className="text-xs">
                              super admin
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {usuario.email}
                          {usuario.crmv && ` · CRMV ${usuario.crmv}${usuario.ufCrmv ?? ''}`}
                        </p>
                      </TableCell>
                      <TableCell>
                        {usuario.ambiente ? (
                          <span
                            className={
                              usuario.ambiente.ativo ? 'text-foreground' : 'text-destructive'
                            }
                          >
                            {usuario.ambiente.nome}
                            {!usuario.ambiente.ativo && ' (desativado)'}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {usuario.cargo?.nome ?? '—'}
                      </TableCell>
                      <TableCell>
                        {usuario.ativo ? (
                          <Badge
                            variant="outline"
                            className="text-emerald-600 border-emerald-600/40"
                          >
                            Ativo
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="text-destructive border-destructive/40"
                          >
                            Desativado
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Ações de ${usuario.nomeCompleto}`}
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {podeVerCreditos && (
                              <DropdownMenuItem onClick={() => setCreditosDe(usuario)}>
                                <Coins className="w-4 h-4" />
                                Créditos
                              </DropdownMenuItem>
                            )}

                            {podeEditar && (
                              <>
                                <DropdownMenuItem
                                  onClick={() => {
                                    setEmEdicao(usuario);
                                    setFormAberto(true);
                                  }}
                                >
                                  <Pencil className="w-4 h-4" />
                                  Editar
                                </DropdownMenuItem>

                                <DropdownMenuItem onClick={() => setCargoDe(usuario)}>
                                  <KeyRound className="w-4 h-4" />
                                  Cargo
                                </DropdownMenuItem>

                                {/* A própria conta não oferece desativar: o
                                    servidor recusa (409) e o admin ficaria sem
                                    forma de voltar. */}
                                {usuario.id !== me?.usuario.id &&
                                  (usuario.ativo ? (
                                    <DropdownMenuItem
                                      onClick={() => setADesativar(usuario)}
                                      className="text-destructive focus:text-destructive"
                                    >
                                      <PowerOff className="w-4 h-4" />
                                      Desativar
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem
                                      onClick={() => void alternarAtivo(usuario, true)}
                                    >
                                      <Power className="w-4 h-4" />
                                      Reativar
                                    </DropdownMenuItem>
                                  ))}
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <Paginacao
              pagina={data.pagina}
              totalPaginas={data.totalPaginas}
              total={data.total}
              porPagina={data.porPagina}
              aoMudarPagina={setPagina}
            />
          </>
        )}
      </div>

      <UsuarioFormDialogo
        aberto={formAberto}
        aoFechar={() => setFormAberto(false)}
        {...(emEdicao ? { usuario: emEdicao } : {})}
      />

      <ExportarUsuariosDialogo
        aberto={exportarAberto}
        aoFechar={() => setExportarAberto(false)}
        {...(busca.trim() ? { busca: busca.trim() } : {})}
        {...(ambienteId !== TODOS ? { tenantId: ambienteId } : {})}
        {...(situacao !== TODOS ? { ativo: situacao as 'true' | 'false' } : {})}
      />

      {cargoDe && (
        <CargoDoUsuarioDialogo aberto aoFechar={() => setCargoDe(null)} usuario={cargoDe} />
      )}

      {creditosDe && (
        <CreditosDoUsuarioDialogo
          aberto
          aoFechar={() => setCreditosDe(null)}
          usuario={creditosDe}
        />
      )}

      <AlertDialog open={Boolean(aDesativar)} onOpenChange={(v) => !v && setADesativar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desativar {aDesativar?.nomeCompleto}?</AlertDialogTitle>
            <AlertDialogDescription>
              O acesso é cortado na próxima ação dele, sem precisar sair e entrar de novo. Nenhum
              dado é apagado, e reativar devolve o acesso.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => aDesativar && void alternarAtivo(aDesativar, false)}>
              Desativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
